import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';
import get from 'lodash.get';
import tracking from 'dibs-tracking';

// TODO Shanie: FINDING-14024 - Remove "Old" components post A/B Test
import { HpRespInformationalContentRelayContainer } from './HpRespInformationalContent/HpRespInformationalContent';
import { HpRespHeroModuleOld } from './HpRespHeroModuleOld/HpRespHeroModuleOld';
import { HpRespTopHeroBannerModule } from './HpRespTopHeroBannerModule/HpRespTopHeroBannerModule';
import { HpRespRecentlyViewedItemsLoader } from './HpRespRecentlyViewedItems/HpRespRecentlyViewedItemsLoader';
import { HpRespLocationsModule } from './HpRespLocationsModule/HpRespLocationsModule';
import { HpRespPersonalizedCategoriesLoader } from './HpRespPersonalizedCategories/HpRespPersonalizedCategoriesLoader';
import { HpRespPromoModulePrimaryOld } from './HpRespPromoModuleOld/HpRespPromoModulePrimaryOld';
import { HpRespCollectionsModuleOld } from './HpRespCollectionsModuleOld/HpRespCollectionsModuleOld';
import { HpRespFeaturedModule } from './HpRespFeaturedModule/HpRespFeaturedModule';
import { HpRespPromoModuleSecondaryOld } from './HpRespPromoModuleOld/HpRespPromoModuleSecondaryOld';
import { HpRespEditorialModuleOld } from './HpRespEditorialModuleOld/HpRespEditorialModuleOld';
import { HpRespShopByModule } from './HpRespShopByModule/HpRespShopByModule';
import { HomePageContext } from './HomePageContext';
import { ShopLocalNotificationLoader } from '../notifications/shopLocal/ShopLocalNotificationLoader';
import { connectContext } from '../context/helpers/connectContext';
import { getItemsPerPage } from './helpers/getItemsPerPage';
import {
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-buyer-layout/exports/regionalInfoHelpers';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';

const NUM_MOBILE_LOCATIONS = 1;
const NUM_RESP_LOCATIONS = 3;

const HpRespLayoutComponent = props => {
    const [hasFiredPageViewTracking, setHasFiredPageViewTracking] = useState(false);
    const [userId, setUserId] = useState(null);
    const [isClient, setIsClient] = useState(null);

    const { relay, isLoggedInUser, viewer, isMobile, isTablet } = props;
    const user = get(viewer, 'user') || null;

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: true });
            relay.refetch(
                fragmentVariables => ({
                    ...fragmentVariables,
                    fetchInitialHomepage: false,
                    isClient: true,
                    isMobile,
                    isTrade: false,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    userCountryCode: getUserSessionCountryCode(),
                    zipCode: getUserZipCode(),
                    localRecentHistoryItemIds,
                    fetchUserRecentHistoryItem:
                        localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
                    ...additionalVariables,
                }),
                { fetchInitialHomepage: true },
                () => setIsClient(true)
            );
        },
        [userId, relay, isMobile]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            setUserId(cookieUserId);
        }
    }, [userId]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        if (!hasFiredPageViewTracking) {
            setHasFiredPageViewTracking(true);
            tracking.trackPageview();
        }
        setUserData();
        addLoginHandlers(data => {
            if (data.user && data.user.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload(true);
                return;
            }
            setUserData();
        });
    }, [setUserData, hasFiredPageViewTracking, setHasFiredPageViewTracking]);

    const { homepage = {} } = viewer;

    const itemsPerPage = getItemsPerPage({ isMobile, isTablet });
    const { personalization = null } = homepage;
    const showLoggedInModules = isLoggedInUser || userId;
    const showPersonalizedCategoriesModule = isClient && showLoggedInModules;
    const pageSetOffset = !isMobile;

    return (
        <Fragment>
            <HpRespInformationalContentRelayContainer viewer={viewer} />
            <HpRespTopHeroBannerModule homepage={homepage} />
            <HpRespHeroModuleOld homepage={homepage} />
            {isClient && (
                <HpRespRecentlyViewedItemsLoader
                    user={get(viewer, 'user') || null}
                    itemsPerPage={itemsPerPage}
                    showFullHeader
                    additionalSpacing={isMobile}
                />
            )}
            {showPersonalizedCategoriesModule && (
                <HpRespPersonalizedCategoriesLoader
                    personalization={personalization}
                    viewer={viewer}
                    hasBorder={!isMobile}
                    hasNoPageGutter={isMobile}
                    pageSetOffset={pageSetOffset}
                />
            )}
            <HpRespPromoModulePrimaryOld homepage={homepage} />
            <HpRespCollectionsModuleOld homepage={homepage} />
            <HpRespFeaturedModule homepage={homepage} />
            <HpRespPromoModuleSecondaryOld homepage={homepage} />
            <HpRespEditorialModuleOld homepage={homepage} />
            <HpRespLocationsModule
                homepage={homepage}
                viewer={viewer || null}
                additionalTopSpacing={isMobile}
                numOfVisibleLocations={isMobile ? NUM_MOBILE_LOCATIONS : NUM_RESP_LOCATIONS}
                pageSetOffset={pageSetOffset}
                showLocationLink={isMobile}
                itemsPerPage={itemsPerPage}
            />
            <HpRespShopByModule homepage={homepage} />
            <ShopLocalNotificationLoader environment={relay.environment} />
        </Fragment>
    );
};

HpRespLayoutComponent.propTypes = {
    isLoggedInUser: PropTypes.bool,
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
};

function mapContextToProps({ isLoggedInUser, isMobile, isTablet }) {
    return {
        isMobile,
        isTablet,
        isLoggedInUser,
    };
}

const HpRespLayoutWithContext = connectContext(
    HomePageContext,
    mapContextToProps
)(HpRespLayoutComponent);

export const HpRespLayout = createRefetchContainer(
    HpRespLayoutWithContext,
    {
        viewer: graphql`
            fragment HpRespLayout_viewer on Viewer {
                ...HpRespPersonalizedCategories_viewer
                homepage(rootId: $homepageId) {
                    ...HpRespTopHeroBannerModule_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespHeroModuleOld_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespPromoModulePrimaryOld_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespCollectionsModuleOld_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespFeaturedModule_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespPromoModuleSecondaryOld_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespEditorialModuleOld_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespShopByModule_homepage @include(if: $fetchInitialHomepage)
                    ...HpRespLocationsModule_homepage @include(if: $fetchInitialHomepage)
                    personalization(userId: $userId, first: 3, numItems: 3)
                        @include(if: $hasUserId) {
                        ...HpRespPersonalizedCategories_personalization
                    }
                }
                ...HpRespLocationsModule_viewer
                ...HpRespInformationalContent_viewer @include(if: $fetchInitialHomepage)
                user(userId: $userId) @include(if: $hasUserId) {
                    ...HpRespRecentlyViewedItems_user
                }
            }
        `,
    },
    graphql`
        query HpRespLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $isClient: Boolean!
            $isTrade: Boolean!
            $isMobile: Boolean!
            $fetchInitialHomepage: Boolean!
            $userCountryCode: String
            $zipCode: String
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
        ) {
            viewer {
                ...HpRespLayout_viewer
            }
        }
    `
);
